<template>
    <div class="flex flex-col w-full p-5 gap-1">
        <div>
            <app-button :label="$t('organizations.newUser')" @click="newUser" v-if="!readOnly"></app-button>
        </div>
        <table class="text-xs border">
            <thead>
                <th class="p-1 border" style="width: 24px"></th>
                <th class="p-1 border text-left" style="width: 40%">{{ $t('commons.name') }}</th>
                <th class="p-1 border" style="width: 6em">{{ $t('commons.phone') }}</th>
                <th class="p-1 border">{{ $t('commons.email') }}</th>
                <th class="p-1 border" style="width: 8em">{{ $t('organizations.lastConnection') }}</th>
            </thead>
            <tr>
                <td class="p-1 border">
                    <icon-crown class="text-yellow-500" :title="$t('organizations.owner')"></icon-crown>
                </td>
                <td class="p-1 border">
                    {{ organization.owner.lastName }}
                    {{ organization.owner.firstName }}
                </td>
                <td class="p-1 border">
                    <app-phone :number="organization.owner.phone" />
                </td>
                <td class="p-1 border">
                    <app-mail :email="organization.owner.email" />
                </td>
                <td class="p-1 border">
                    <div class="flex gap-1">
                        <span v-if="organization.owner.lastConnection" :title="$t('organizations.hasAccount')">
                            <icon-user class="text-green-600" width="16px" height="16px"></icon-user>
                        </span>
                        <span v-else :title="$t('organizations.hasNoAccount')">
                            <icon-account-off class="text-red-600" width="16px" height="16px"></icon-account-off>
                        </span>
                        {{ organization.owner.lastConnection | humanizeDate }}
                    </div>
                </td>
            </tr>
            <tr v-for="user in users">
                <td class="p-1 border"></td>
                <td class="p-1 border">
                    <a href="#" class="underline" @click="editUser(user)">{{ user.lastName }} {{ user.firstName }}</a>
                </td>
                <td class="p-1 border">
                    <app-phone :number="user.phone" />
                </td>
                <td class="p-1 border">
                    <app-mail :email="user.email" />
                </td>
                <td class="p-1 border">
                    <div class="flex gap-1">
                        <span v-if="user.authUserId" :title="$t('organizations.hasAccount')">
                            <icon-user class="text-green-600" width="16px" height="16px"></icon-user>
                        </span>
                        <span v-else :title="$t('organizations.hasNoAccount')">
                            <icon-account-off class="text-red-600" width="16px" height="16px"></icon-account-off>
                        </span>
                        {{ user.lastConnection | humanizeDate }}
                    </div>
                </td>
            </tr>
        </table>
        <app-popup
            ref="popup"
            :title="editedUser.id ? $t('organizations.updateUser') : $t('organizations.newUser')"
            :show-header="true"
            popup-extra-class="left-50"
        >
            <ValidationObserver v-slot="{ invalid, errors, dirty }" ref="observer" class="p-2">
                <form class="flex flex-col gap-2">
                    <app-input-text
                        :label="$t('users.lastName')"
                        v-model="editedUser.lastName"
                        :required="true"
                        class="col-span-2"
                    />
                    <app-input-text :label="$t('users.firstName')" v-model="editedUser.firstName" class="col-span-2" />
                    <app-phone-input v-model="editedUser.phone" class="col-span-2" />
                    <app-input-text
                        :label="$t('users.email')"
                        v-model="editedUser.email"
                        type="email"
                        :required="true"
                        class="col-span-2"
                        @paste="onPasteMail"
                    />
                    <app-footer @click="saveUser" :disabled="invalid" class="mt-2">
                        <template slot="left" v-if="editedUser.id">
                            <app-button
                                variant="danger"
                                @click="removeOrganizationUser(editedUser.id)"
                                :label="$t('commons.removeShort')"
                                size="mini"
                            ></app-button>
                        </template>
                    </app-footer>
                </form>
            </ValidationObserver>
        </app-popup>
    </div>
</template>

<script>
import AppInputText from '@/components/appInputText/AppInputText.vue';
import {
    getOrganization,
    getOrganizationUsers,
    insertOrganizationUser,
    removeOrganizationUser,
    updateOrganizationUser,
} from '@/features/organizations/organizations.service';
import AppPhone from '@/components/appPhone/AppPhone.vue';
import { getLoggedUser } from '@/features/tokens/token.service';
import AppPhoneInput from '@/components/appPhoneInput/AppPhoneInput.vue';
import AppMail from '@/components/appMail/AppMail.vue';
import AppButton from '@/components/appButton/AppButton.vue';
import AppPopup from '@/components/app-popup/AppPopup.vue';
import AppFooter from '@/components/appFooter/AppFooter.vue';
import { updateBreadCrumbs } from '@/state/state';

export default {
    components: { AppFooter, AppPhoneInput, AppInputText, AppPopup, AppMail, AppPhone, AppButton },
    created() {
        this.init();
    },
    watch: {
        $route() {
            this.init();
        },
    },
    methods: {
        async refreshUsers() {
            this.users = await getOrganizationUsers(this.$route.params.organizationId);
        },
        onPasteMail() {
            setTimeout(() => {
                this.editedUser.email = this.editedUser.email
                    ? this.editedUser.email.replace('mailto:', '').replaceAll(' ', '')
                    : '';
            }, 100);
        },
        editUser(user) {
            if (this.readOnly) return;
            this.editedUser = user;
            this.$refs.popup.open();
        },
        newUser() {
            this.editedUser = {};
            this.$refs.popup.open();
        },
        async removeOrganizationUser() {
            await removeOrganizationUser(this.$route.params.organizationId, this.editedUser.id);
            this.editedUser = {};
            this.$refs.popup.close();
            return this.refreshUsers();
        },
        async saveUser() {
            if (this.editedUser.id) {
                await updateOrganizationUser(this.$route.params.organizationId, {
                    id: this.editedUser.id,
                    firstName: this.editedUser.firstName,
                    lastName: this.editedUser.lastName,
                    phone: this.editedUser.phone,
                    email: this.editedUser.email,
                });
            } else {
                await insertOrganizationUser(this.$route.params.organizationId, {
                    firstName: this.editedUser.firstName,
                    lastName: this.editedUser.lastName,
                    phone: this.editedUser.phone,
                    email: this.editedUser.email,
                });
            }
            this.$refs.popup.close();
            return this.refreshUsers();
        },
        async init() {
            this.organization = await getOrganization(this.$route.params.organizationId);
            updateBreadCrumbs({ organizationName: this.organization.name });
            const token = getLoggedUser();
            const isRoot = token && token.roles.includes('root');
            const isOwner = this.organization.ownerId === token.id;
            this.readOnly = !isRoot && !isOwner;
            await this.refreshUsers();
        },
    },
    data() {
        return {
            users: [],
            readOnly: true,
            editedUser: {},
            organization: { owner: {} },
        };
    },
};
</script>
